import { useState, useEffect } from "react";
import BaseModal from "./BaseModal";
import { estimateExtractionApi } from "@services/api/estimateExtraction";
import { showNotification } from "context/notification.service";

type Reminder = {
  id: string;
  type: string;
  scheduled_at: string;
  task_id?: string;
  status: string;
};

type Props = {
  onClose: () => void;
  tokenId: string;
  onUpdate?: () => void;
};

const LABEL_MAP: Record<string, string> = {
  r1: "Rappel 1 (24h)",
  r2: "Rappel 2 (48h)",
  r3: "Rappel urgent (J-2)",
  cancel: "Annulation automatique",
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "En attente", color: "text-orange-600 bg-orange-50" },
  sent: { label: "Envoyé", color: "text-green-600 bg-green-50" },
  skipped: { label: "Ignoré", color: "text-gray-400 bg-gray-50" },
  cancelled: { label: "Annulé", color: "text-red-500 bg-red-50" },
};

function formatDT(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" }) +
    " à " +
    d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  );
}

export default function DevisReminderModal({ onClose, tokenId, onUpdate }: Props) {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    estimateExtractionApi.getTokenReminders(tokenId).then(({ reminders }) => {
      setReminders(reminders);
      setFetching(false);
    }).catch(() => setFetching(false));
  }, [tokenId]);

  const handleCancel = async (reminderId: string) => {
    setLoading(reminderId);
    try {
      const { reminders: updated } = await estimateExtractionApi.updateReminder(
        tokenId, reminderId, { status: "cancelled" },
      );
      setReminders(updated);
      onUpdate?.();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'annulation" });
    } finally {
      setLoading("");
    }
  };

  const handleReschedule = async (reminderId: string) => {
    if (!editDate || !editTime) return;
    setLoading(reminderId);
    try {
      const newDt = new Date(`${editDate}T${editTime}:00`);
      const { reminders: updated } = await estimateExtractionApi.updateReminder(
        tokenId, reminderId, { scheduled_at: newDt.toISOString() },
      );
      setReminders(updated);
      setEditingId(null);
      onUpdate?.();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la modification" });
    } finally {
      setLoading("");
    }
  };

  const handleCancelAll = async () => {
    setLoading("all");
    try {
      const { reminders: updated } = await estimateExtractionApi.cancelAllReminders(tokenId);
      setReminders(updated);
      onUpdate?.();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'annulation" });
    } finally {
      setLoading("");
    }
  };

  const handleSendNow = async () => {
    setLoading("send");
    try {
      await estimateExtractionApi.sendReminderNow(tokenId);
      showNotification({ type: "success", message: "Rappel envoyé" });
      onUpdate?.();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setLoading("");
    }
  };

  const hasPending = reminders.some((r) => r.status === "pending");

  return (
    <BaseModal title="Rappels devis" onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-3 p-4">
        {fetching ? (
          <p className="text-sm text-gray-400">Chargement...</p>
        ) : reminders.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun rappel configuré</p>
        ) : (
          reminders.map((r) => {
            const st = STATUS_LABELS[r.status] || STATUS_LABELS.pending;
            const isPending = r.status === "pending";
            const isCancel = r.id === "cancel";

            return (
              <div key={r.id} className={`rounded-lg border p-3 ${isCancel && isPending ? "border-red-200 bg-red-50/50" : ""}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{LABEL_MAP[r.id] || r.id}</div>
                    <div className="text-xs text-gray-500">{formatDT(r.scheduled_at)}</div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${st.color}`}>
                    {st.label}
                  </span>
                </div>

                {isPending && !isCancel && (
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => {
                        const d = new Date(r.scheduled_at);
                        setEditDate(d.toISOString().slice(0, 10));
                        setEditTime(d.toTimeString().slice(0, 5));
                        setEditingId(r.id);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                      disabled={!!loading}
                    >
                      Modifier horaire
                    </button>
                    <button
                      onClick={() => handleCancel(r.id)}
                      className="text-xs text-red-500 hover:underline"
                      disabled={!!loading}
                    >
                      {loading === r.id ? "..." : "Annuler"}
                    </button>
                  </div>
                )}

                {editingId === r.id && (
                  <div className="flex gap-2 mt-2 items-center">
                    <input
                      type="date"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                    />
                    <input
                      type="time"
                      value={editTime}
                      onChange={(e) => setEditTime(e.target.value)}
                      className="text-xs border rounded px-2 py-1"
                    />
                    <button
                      onClick={() => handleReschedule(r.id)}
                      className="text-xs bg-black text-white px-2 py-1 rounded"
                      disabled={!!loading}
                    >
                      OK
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="text-xs text-gray-500"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}

        <div className="flex flex-col gap-2 border-t pt-3">
          <button
            onClick={handleSendNow}
            disabled={!!loading}
            className="text-sm text-blue-600 hover:underline text-left"
          >
            {loading === "send" ? "Envoi en cours..." : "Renvoyer un rappel maintenant"}
          </button>
          {hasPending && (
            <button
              onClick={handleCancelAll}
              disabled={!!loading}
              className="text-sm text-red-500 hover:underline text-left"
            >
              {loading === "all" ? "Annulation..." : "Annuler tous les rappels"}
            </button>
          )}
        </div>

        <div className="flex justify-end border-t pt-3">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">
            Fermer
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
